import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import { CommonOnUpdate, CommonOnError, CommonRequired } from 'types/props';
import { LegalInvestigationsInvestigatingAgencyModel } from 'forms/sections/legal/investigation/history/types';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import Show from 'components/Form/Show';
import Textarea from 'components/Form/Textarea';
import { isPVQForm } from 'helpers/form';
import Field from 'components/Form/Field';
import SelectDropdown from 'components/Form/SelectDropdown';
import { setAttribute } from 'components/Form/shared/setAttribute';
import Dropdown from 'components/Form/Dropdown';
import { getAgencyPVQOptions } from 'constants/enums/legalOptions';

interface Props
  extends Partial<LegalInvestigationsInvestigatingAgencyModel>,
  ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  disabled?: boolean
  attribute?: string
  className?: string
  formType?: string
}

export default class InvestigatingAgency extends ValidationElement<Props> {
  constructor(props) {
    super(props);

    this.state = {
      uid: `${this.props.Agency}-${super.guid()}`,
    };
  }

  update = (queue: Partial<LegalInvestigationsInvestigatingAgencyModel>) => {
    this.props.onUpdate({
      Agency: this.props.Agency,
      Explanation: this.props.Explanation,
      ...queue,
    });
  };

  updateAgency = (values: LegalInvestigationsInvestigatingAgencyModel['Agency']) => {
    this.update({
      Agency: values,
    });
  };

  updateExplanation = (values: LegalInvestigationsInvestigatingAgencyModel['Explanation']) => {
    this.update({
      Explanation: values,
    });
  };

  render() {
    const isPVQ = isPVQForm(this.props.formType);

    return isPVQ ? (
      <div className={this.props.className}>

        <Dropdown
          {...this.props.Agency}
          placeholder={this.props.Agency?.value}
          attribute={setAttribute(this.props.attribute, 'agency')}
          name="agency"
          onUpdate={this.updateAgency}
          onError={this.props.onError}
          sort={false}
          value={this.props.Agency?.value}
          required={this.props.required}
          disabled={this.props.disabled}
          suggestionsRenderProvided
        >
          {getAgencyPVQOptions().map((option) => (
            <option key={`name-${option.label}`} label={option.label} value={option.value}>
              {option.label}
            </option>
          ))}
        </Dropdown>

      </div>
    ) : (
      <div className={this.props.className}>
        <RadioGroup
          className="investigative-agencies option-list option-list-vertical"
          selectedValue={(this.props.Agency || {}).value}
          onError={this.props.onError}
          required={this.props.required}
          disabled={this.props.disabled}
          attribute={`${this.props.attribute}.Agency`}
        >
          <Radio
            label={i18n.t('legal.investigations.history.label.agency.dod')}
            value="U.S. Department of Defense"
            className="investigative-agency-dod"
            onUpdate={this.updateAgency}
            onError={this.props.onError}
          />
          <Radio
            label={i18n.t('legal.investigations.history.label.agency.dos')}
            value="U.S. Department of State"
            className="investigative-agency-dos"
            onUpdate={this.updateAgency}
            onError={this.props.onError}
          />
          <Radio
            label={i18n.t('legal.investigations.history.label.agency.opm')}
            value="U.S. Office of Personnel Management"
            className="investigative-agency-opm"
            onUpdate={this.updateAgency}
            onError={this.props.onError}
          />
          <Radio
            label={i18n.t('legal.investigations.history.label.agency.fbi')}
            value="Federal Bureau of Investigation"
            className="investigative-agency-fbi"
            onUpdate={this.updateAgency}
            onError={this.props.onError}
          />
          <Radio
            label={i18n.t('legal.investigations.history.label.agency.dot')}
            value="U.S. Department of Treasury"
            className="investigative-agency-dot"
            onUpdate={this.updateAgency}
            onError={this.props.onError}
          />
          <Radio
            label={i18n.t('legal.investigations.history.label.agency.dhs')}
            value="U.S. Department of Homeland Security"
            className="investigative-agency-dhs"
            onUpdate={this.updateAgency}
            onError={this.props.onError}
          />
          <Radio
            label={i18n.t('legal.investigations.history.label.agency.foreign')}
            value="Foreign government"
            className="investigative-agency-foreign"
            onUpdate={this.updateAgency}
            onError={this.props.onError}
          />
          <Radio
            label={i18n.t('legal.investigations.history.label.agency.other')}
            value="Other"
            className="investigative-agency-other"
            onUpdate={this.updateAgency}
            onError={this.props.onError}
          />
        </RadioGroup>

        <Show
          when={['U.S. Department of Treasury', 'Foreign government', 'Other'].includes(
            (this.props.Agency || {}).value
          )}
        >
          {i18n.t('legal.investigations.history.heading.agencyExplanation')}
          <Textarea
            name="Explanation"
            {...this.props.Explanation}
            className="legal-investigations-history-agency-explanation"
            onUpdate={this.updateExplanation}
            onError={this.props.onError}
            required={this.props.required}
            disabled={this.props.disabled}
            attribute={`${this.props.attribute}.Explanation`}
          />
        </Show>
      </div>
    );
  }
}

InvestigatingAgency.defaultProps = {
  className: 'investigative-agency',
  Agency: {},
  Explanation: {},
  onUpdate: (queue) => { },
  onError: (value, arr: ErrorCode[]) => arr,
};

InvestigatingAgency.errors = [];
